import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Parser } from 'expr-eval';
import { uniq } from 'lodash-es';
import { CalculatedSignalProvider } from './calculated-signal-provider';
import { EncodingSlot } from '../hgm-setup/src/module/param/core/encoding-slot';
import { Log, LogFile, LogHeader } from '../hgm-setup/src/module/param/core/logging-types';
import { ScalarWrapper } from '../hgm-setup/src/module/param/core/scalar-wrapper';
import { Slot } from '../hgm-setup/src/module/param/core/slot';
import { VectorParam } from '../hgm-setup/src/module/param/core/vector-param';
import { DisplaySlotLocalizer } from '../hgm-setup/src/module/param/services/display-slot-localizer';
import { ParamDefDisplaySlotLocalizer } from '../hgm-setup/src/module/param/services/param-def-display-slot-localizer';
import { ParamRegistry } from '../hgm-setup/src/module/param/services/param-registry';
import { LoggingParamDef } from '../hgm-setup/src/module/param/util/param-system-def-types';
import { LogFileProvider } from '../hgm-setup/src/module/util/log-file-provider';
import { TranslationManager } from '../hgm-setup/src/module/util/translation-manager';

export interface Signal {
    key: string;
    name: string;
    unit: string;
    data: [number, number][]; // localized units
    slot: Slot;
}

export interface SignalNoData {
    key: string;
    name: string;
    unit: string;
    data: [number, number][]; // localized units
    hidden?: boolean;
}

export interface DataSet {
    zeroDate: Date;
    signals: { [key: string]: Signal };
}

interface ParamSignalParam {
    key: string;
    slot: Slot;
}

interface ParamSignal {
    param: ParamSignalParam;
    name: string;
    data: [number, number][]; // raw units
}

interface ParamDataSet {
    zeroDate: Date;
    signals: { [key: string]: ParamSignal };
}

const dtcRegex = new RegExp(/^([PCBU])([0-3][0-9A-F]{3})$/);
const dtcSlot = new EncodingSlot(
    [
        [0, 'Clear'],
        [1, 'Set'],
    ],
    undefined,
    undefined,
);

export interface OkResult {
    paramSet: ParamDataSet;
    file: LogFile;
    error?: undefined;
}

export interface ErrResult<T> {
    paramSet?: undefined;
    file: LogFile;
    error: any;
}

type Kvt = Log['kvt'][0];
type ValueTime = Pick<Kvt, 'value' | 'time'>;

const makeKvtMap = (kvts: Kvt[]) => {
    const map = new Map<string, ValueTime[]>();

    for (const kvt of kvts) {
        if (!map.has(kvt.key)) {
            map.set(kvt.key, []);
        }
        map.get(kvt.key)!.push({ value: kvt.value, time: kvt.time });
    }

    return map;
};

const parseValueTime = (slot: Slot, vts: ValueTime[], logTime: number): [number, number][] =>
    vts.map((vt) => {
        const t = parseFloat(vt.time as string) - logTime;
        return [t, slot.toRaw(vt.value) ?? NaN];
    });

@Injectable({ providedIn: 'root' })
export class DataProvider {
    readonly result = computed(() => this.resultSignal());
    readonly data = computed(() => this.dataSignal());

    private readonly resultSignal = signal<OkResult | ErrResult<unknown> | undefined>(undefined);
    private readonly dataSignal = signal<DataSet | undefined>(undefined);
    private readonly dsl = inject(DisplaySlotLocalizer);
    private readonly paramRegistry = inject(ParamRegistry);
    private readonly translationManager = inject(TranslationManager);
    private readonly calcProvider = inject(CalculatedSignalProvider);

    constructor() {
        const logProvider = inject(LogFileProvider);
        logProvider.regReadyObs.subscribe({ next: this.onFile });
        this.paramDsl.localizationsChangeObs.subscribe({ next: this.update });
        effect(() => {
            this.calcProvider.signals();
            this.update();
        });
    }

    update = () => {
        if (!this.resultSignal()?.paramSet) {
            this.dataSignal.set(undefined);
            return;
        }

        const paramSet = this.resultSignal()!.paramSet!;
        const set: DataSet = { zeroDate: paramSet.zeroDate, signals: {} };
        for (const key of Object.keys(paramSet.signals)) {
            const paramSignal = paramSet.signals[key];
            const slot = this.dsl.localize(paramSignal.param.slot);
            set.signals[key] = {
                key,
                name: paramSignal.name,
                unit: slot.unit,
                data: paramSignal.data.map((d) => [d[0], slot.toEngrFloat(d[1]) ?? NaN]),
                slot,
            };
        }

        const toCamelCase = (str: string) => {
            let s = str.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
            if (!s) return 'signal';
            let words = s.split(/\s+/);
            let result =
                words[0].toLowerCase() +
                words
                    .slice(1)
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                    .join('');
            if (/^[0-9]/.test(result)) result = '_' + result;
            return result;
        };

        // --- INJECT CALCULATED SIGNALS (DAG LOCF) ---
        const calcSignals = this.calcProvider.signals();
        if (calcSignals && calcSignals.length > 0) {
            const parser = new Parser();
            let currentT = 0; // Exposed to math functions for dt derivations

            let lpfStates: number[] = [];
            let lpfIndex = 0;
            parser.functions.lpf = (val: number, alpha: number) => {
                const i = lpfIndex++;
                if (lpfStates[i] === undefined) lpfStates[i] = val; // Initialize with first value
                lpfStates[i] = lpfStates[i] + alpha * (val - lpfStates[i]);
                return lpfStates[i];
            };

            let smaStates: number[][] = [];
            let smaIndex = 0;
            parser.functions.sma = (val: number, windowSize: number) => {
                const i = smaIndex++;
                if (!smaStates[i]) smaStates[i] = [];
                const buf = smaStates[i];
                buf.push(val);
                if (buf.length > windowSize) buf.shift();
                return buf.reduce((sum, v) => sum + v, 0) / buf.length;
            };

            let rocStates: { prevVal: number; prevT: number }[] = [];
            let rocIndex = 0;
            parser.functions.roc = (val: number) => {
                const i = rocIndex++;
                if (!rocStates[i]) rocStates[i] = { prevVal: val, prevT: currentT };
                const state = rocStates[i];
                const dt = currentT - state.prevT;
                let rate = 0;

                if (dt > 0) {
                    rate = (val - state.prevVal) / dt;
                }

                state.prevVal = val;
                state.prevT = currentT;
                return rate;
            };

            let intStates: { sum: number; prevT: number }[] = [];
            let intIndex = 0;
            parser.functions.integral = (val: number) => {
                const i = intIndex++;
                if (!intStates[i]) intStates[i] = { sum: 0, prevT: currentT };
                const state = intStates[i];
                const dt = currentT - state.prevT;

                if (dt > 0) {
                    state.sum += val * dt;
                }

                state.prevT = currentT;
                return state.sum;
            };

            let pending = [...calcSignals];
            let madeProgress = true;

            // Topological Resolution
            while (pending.length > 0 && madeProgress) {
                madeProgress = false;
                const remaining: typeof pending = [];

                for (const cSig of pending) {
                    try {
                        const expr = parser.parse(cSig.formula);
                        const deps = expr.variables();

                        // Check if all needed dependencies exist in 'set.signals'
                        const depsMissing = deps.some((d) => d !== 'time' && !set.signals[d]);
                        if (depsMissing) {
                            remaining.push(cSig);
                            continue;
                        }

                        // Collect unique timestamps from all dependencies to form master timeline
                        const timeSet = new Set<number>();
                        for (const d of deps) {
                            if (d !== 'time' && set.signals[d]) {
                                set.signals[d].data.forEach((pt) => timeSet.add(pt[0]));
                            }
                        }

                        let sortedTimes = Array.from(timeSet).sort((a, b) => a - b);

                        // Fallback timeline just in case no signals were referenced (e.g. '5 + 5' or pure 'time' based math)
                        if (sortedTimes.length === 0 && Object.keys(set.signals).length > 0) {
                            sortedTimes = Object.values(set.signals)[0].data.map((pt) => pt[0]);
                        }

                        // LOCF State Trackers
                        const locfState: Record<string, { idx: number; val: number }> = {};
                        for (const d of deps) {
                            if (d !== 'time') locfState[d] = { idx: 0, val: 0 };
                        }

                        const generatedData: [number, number][] = [];

                        // Reset states specifically for THIS formula's timeline evaluation
                        lpfStates = [];
                        smaStates = [];
                        rocStates = [];
                        intStates = [];

                        // Walk the timeline
                        for (const t of sortedTimes) {
                            currentT = t;
                            const context: Record<string, number> = { time: t };
                            for (const d of deps) {
                                if (d !== 'time') {
                                    const sigData = set.signals[d].data;
                                    let state = locfState[d];

                                    // Advance pointer until we pass our current time
                                    while (state.idx < sigData.length - 1 && sigData[state.idx + 1][0] <= t) {
                                        state.idx++;
                                    }
                                    if (sigData.length > 0) {
                                        state.val = sigData[state.idx][1];
                                    }
                                    context[d] = state.val;
                                }
                            }

                            // Evaluate using Context
                            lpfIndex = 0;
                            smaIndex = 0;
                            rocIndex = 0;
                            intIndex = 0;
                            const result = expr.evaluate(context);
                            if (Number.isFinite(result)) {
                                generatedData.push([t, result]);
                            }
                        }

                        // Inject the generated timeline
                        const camelKey = toCamelCase(cSig.name);
                        set.signals[camelKey] = {
                            key: camelKey,
                            name: cSig.name,
                            unit: cSig.unit,
                            data: generatedData,
                            slot: { precision: cSig.precision, unit: cSig.unit } as any, // Unique object reference automatically separates Y-Axes during Lodash uniq()
                        };

                        madeProgress = true;
                    } catch (e) {
                        console.warn(`Failed to process Calculated Signal ${cSig.name}`, e);
                    }
                }
                pending = remaining;
            }
        }

        this.dataSignal.set(set);
    };

    onFile = (file: LogFile) => {
        if (!file) {
            this.resultSignal.set(undefined);
            this.update();
            return;
        }

        try {
            this.loadEarlyParams(file.log.header.earlyLoadParams);

            const baseLogDate = new Date(file.log.header.logDate);
            const zeroDate = new Date(baseLogDate.getTime() - file.log.header.logTime);

            const paramSet = { zeroDate, signals: this.getSignals(file.log) };

            this.resultSignal.set({ file, paramSet });
        } catch (error) {
            this.resultSignal.set({ file, error });
        }
        this.update();
    };

    private get paramDsl() {
        return this.dsl as ParamDefDisplaySlotLocalizer;
    }

    private loadEarlyParams(params: LogHeader['earlyLoadParams']) {
        for (const [key, value] of Object.entries(params)) {
            const param = this.paramRegistry.paramByKeyWarn(key);
            if (!param) {
                continue;
            }
            param.setSerializableValue(value);
        }
    }

    private getSignals(log: Log) {
        const kvtMap = makeKvtMap(log.kvt);
        if (log.kvt.length === 0) {
            return {};
        }
        const signals: ParamDataSet['signals'] = {};

        // handle regular scalar params and string params
        for (const def of log.header.logDefs) {
            try {
                const vts = kvtMap.get(def.param) || [];
                const param = this.parseStringParam(def, vts) || new ScalarWrapper(this.paramRegistry, def.param);
                const name = this.translationManager.translate(def.label, 'traceLabel');
                const data = parseValueTime(param.slot, vts, log.header.logTime);
                signals[def.param] = { param, name, data };
            } catch (err) {
                console.warn(`Could not make scalar/string wrapper for ${def.param}`);
            }
        }

        // now look for any DTCs that need a fake param
        const dataStartTime = log.kvt.length ? parseFloat(log.kvt[0].time as string) - log.header.logTime : NaN;
        for (const [key, vts] of kvtMap.entries()) {
            if (!signals[key] && key.match(dtcRegex)) {
                let data = parseValueTime(dtcSlot, vts, log.header.logTime);

                const approxPeriod = data.length > 1 ? data[1][0] - data[0][0] : 1e-6;
                const dtcLastClearTime = data[0][0] - approxPeriod;
                if (dataStartTime < dtcLastClearTime) {
                    // create clear state from log start to when DTC was first recorded
                    data = [[dataStartTime, 0], [dtcLastClearTime, 0], ...data];
                }

                signals[key] = {
                    param: { key, slot: dtcSlot },
                    name: key,
                    data,
                };
            }
        }

        return signals;
    }

    private parseStringParam(def: LoggingParamDef, vts: ValueTime[]): ParamSignalParam | undefined {
        const match = /^(\w+)\[\d+\]$/.exec(def.param);
        const key = match ? match[1] : def.param;
        const vector = this.paramRegistry.typedParamByKeyTry(VectorParam, key);
        if (vector && vts.every((vt) => typeof vt.value === 'string')) {
            // looks like a string param, generate an encoding SLOT for it
            const strings = uniq(vts.map((vt) => vt.value)).sort();
            const encoding = Array.from(strings.entries());
            const slot = new EncodingSlot(encoding, undefined, undefined);

            return { key: def.param, slot };
        }
        return undefined;
    }
}
